import { getAdminTenantSlug } from '@/utils/adminAuth'

const STORAGE_KEY = 'agendamentos-app:tenant-slug'
export const TENANT_SLUG_CHANGED_EVENT = 'agendamentos-app:tenant-slug-changed'

/** Prefixo da rota pública de entrada por empresa (ex.: /e/meu-salao). */
export const TENANT_ENTRY_SEGMENT = 'e'

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/**
 * Slug da conta SaaS (enviado como X-Tenant-Slug em todos os pedidos à API).
 * App nativa: slug fixo do build tem prioridade sobre localStorage.
 */
export function getTenantSlug(): string {
  const buildSlug = getBuildTimeTenantSlug()
  if (buildSlug) return buildSlug

  try {
    const fromLs = localStorage.getItem(STORAGE_KEY)?.trim()
    if (fromLs) return fromLs.toLowerCase()
  } catch {
    /* ignore */
  }
  const fromEnv = import.meta.env.VITE_TENANT_SLUG
  if (fromEnv != null && String(fromEnv).trim()) {
    return String(fromEnv).trim().toLowerCase()
  }
  return ''
}

/** true se o utilizador já entrou pelo link /e/slug ou build com VITE_TENANT_SLUG. */
export function hasTenantBound(): boolean {
  return getTenantSlug().length > 0
}

/** Slug embutido no build (app nativa white-label). */
export function getBuildTimeTenantSlug(): string {
  const fromDefine = String(typeof __NATIVE_TENANT_SLUG__ !== 'undefined' ? __NATIVE_TENANT_SLUG__ : '')
    .trim()
    .toLowerCase()
  if (fromDefine) return fromDefine

  const fromEnv = import.meta.env.VITE_TENANT_SLUG
  if (fromEnv != null && String(fromEnv).trim()) {
    return String(fromEnv).trim().toLowerCase()
  }
  return ''
}

/** true em builds Capacitor white-label (TENANT_SLUG no build nativo). */
export function isNativeWhiteLabelBuild(): boolean {
  if (typeof window !== 'undefined' && (window as Window & { __NATIVE_APP__?: boolean }).__NATIVE_APP__) {
    return true
  }
  if (typeof __NATIVE_APP_BUILD__ !== 'undefined' && __NATIVE_APP_BUILD__) return true
  return getBuildTimeTenantSlug().length > 0
}

/** Grava o slug do build na sessão (app nativa abre direto no portal da empresa). */
export function bootstrapTenantFromBuild(): void {
  const slug = getBuildTimeTenantSlug()
  if (slug) setTenantSlug(slug)
}

export function setTenantSlug(slug: string): void {
  const s = String(slug || '').trim().toLowerCase()
  if (!s) return
  try {
    const prev = localStorage.getItem(STORAGE_KEY)
    localStorage.setItem(STORAGE_KEY, s)
    if (prev !== s) {
      window.dispatchEvent(new CustomEvent(TENANT_SLUG_CHANGED_EVENT, { detail: { slug: s } }))
    }
  } catch {
    /* ignore */
  }
}

export function clearTenantSlug(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
    window.dispatchEvent(new CustomEvent(TENANT_SLUG_CHANGED_EVENT, { detail: { slug: '' } }))
  } catch {
    /* ignore */
  }
}

export function normalizeTenantSlugParam(raw: string): string {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80)
}

export function isValidTenantSlugParam(slug: string): boolean {
  return slug.length >= 2 && SLUG_PATTERN.test(slug)
}

/** Caminho do portal de entrada (ex.: /e/bela-vista). */
export function getTenantEntryPath(slug: string): string {
  const s = normalizeTenantSlugParam(slug)
  return `/${TENANT_ENTRY_SEGMENT}/${encodeURIComponent(s)}`
}

/** URL absoluta do portal (para copiar no super-admin). */
export function getTenantPortalAbsoluteUrl(slug: string): string {
  const base = typeof window !== 'undefined' ? window.location.origin : ''
  return `${base}${getTenantEntryPath(slug)}`
}

/**
 * Entrada pública do cliente: portal da empresa se o slug estiver na sessão;
 * caso contrário, landing comercial na raiz.
 */
export function getClientPublicEntryPath(): string {
  const slug = getTenantSlug()
  if (slug) return getTenantEntryPath(slug)
  return '/'
}

/** Após logout do admin: portal da empresa se soubermos o slug; senão ecrã de login admin. */
export function getAdminPostLogoutPath(): string {
  const slug = getTenantSlug() || getAdminTenantSlug()
  if (slug) {
    setTenantSlug(slug)
    return getTenantEntryPath(slug)
  }
  return '/admin'
}

/** Caminho do login de cliente (após escolher «Cliente» no portal). */
export function getClientLoginPath(): string {
  return '/login'
}

/** true em /login, /cadastro, /recuperar-codigo ou portal /e/slug (seleção de tipo de acesso). */
export function isClientLoginPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/'
  if (path === '/' || path === '/login' || path === '/cadastro' || path === '/recuperar-codigo') return true
  const seg = TENANT_ENTRY_SEGMENT.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp(`^/${seg}/[^/]+$`).test(path)
}
